import React, { useMemo } from 'react';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { useApp } from '../../context/AppContext';
import KPICard from '../../components/KPICard/KPICard';
import Card from '../../components/Card/Card';
import FleetMap from '../../components/FleetMap/FleetMap';
import '../../styles/layout.css';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// ─── Color constants ──────────────────────────────────────────────────────────
const STATUS_COLORS = {
  Active:      '#16a34a',
  Stored:      '#d97706',
  Maintenance: '#1246d6',
  'On repair': '#dc2626',
  'On Lease':  '#16a34a',
};

const ROLE_COLORS = {
  PAX:   '#1246d6',
  VIP:   '#7c3aed',
  Cargo: '#d97706',
};

const ENGINE_COLORS = {
  'GE/CFM':      '#1246d6',
  'PW':          '#0698a0',
  'Rolls-Royce': '#7c3aed',
  'Other':       '#8faabb',
};

// ─── Helper: classify engine into family ─────────────────────────────────────
function engineFamily(engine) {
  const e = (engine || '').toUpperCase();
  if (e.startsWith('CF34') || e.startsWith('CFM') || e.startsWith('GE')) return 'GE/CFM';
  if (e.startsWith('PW308') || e.startsWith('PW')) return 'PW';
  if (e.startsWith('RR') || e.startsWith('TRENT') || e.startsWith('BR') || e.startsWith('AE')) return 'Rolls-Royce';
  return 'Other';
}

// ─── Shared chart option factories ───────────────────────────────────────────
const doughnutOpts = (label) => ({
  responsive: true,
  maintainAspectRatio: false,
  cutout: '65%',
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (ctx) => ` ${ctx.label}: ${ctx.parsed} (${Math.round(ctx.parsed / ctx.dataset.data.reduce((a, b) => a + b, 0) * 100)}%)`,
      },
    },
  },
});

const hBarOpts = {
  indexAxis: 'y',
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: {
      grid: { color: '#f0f3f8' },
      ticks: { font: { size: 9.5 }, color: '#8faabb' },
    },
    y: {
      grid: { display: false },
      ticks: { font: { size: 9 }, color: '#3d5166' },
    },
  },
};

// ─── DonutLegend sub-component ────────────────────────────────────────────────
function DonutLegend({ labels, data, colors }) {
  const total = data.reduce((a, b) => a + b, 0) || 1;
  return (
    <div className="dleg">
      {labels.map((l, i) => (
        <div className="dlr" key={l}>
          <div className="dll">
            <div className="dls" style={{ background: colors[i] }} />
            <span style={{ color: 'var(--tx2)' }}>{l}</span>
          </div>
          <span className="dln">
            {data[i]}{' '}
            <span style={{ color: 'var(--tx3)', fontSize: 9 }}>
              {Math.round((data[i] / total) * 100)}%
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Overview page ────────────────────────────────────────────────────────────
export default function Overview() {
  const { operator, status, D, LIVE } = useApp();

  // ── Filtered data ──────────────────────────────────────────────────────────
  const fleet = useMemo(() => {
    let f = D.fleet || [];
    if (operator && operator !== 'All') f = f.filter((a) => a.O === operator);
    if (status && status !== 'All') f = f.filter((a) => a.S === status);
    return f;
  }, [D, operator, status]);

  const util = useMemo(() => {
    let u = D.util || [];
    if (operator && operator !== 'All') u = u.filter((r) => r.op === operator);
    return u;
  }, [D, operator]);

  const mro = useMemo(() => {
    let m = D.mro || [];
    if (operator && operator !== 'All') {
      const regs = new Set(fleet.map((a) => a.R));
      m = m.filter((e) => regs.has(e.regIn));
    }
    return m;
  }, [D, operator, fleet]);

  // ── KPI derivations ────────────────────────────────────────────────────────
  const kpi = useMemo(() => {
    const fleetSize = fleet.length;

    const activeCount = fleet.filter((a) => a.S === 'Active' || a.S === 'On Lease').length;

    const avgDailyHrs =
      util.length > 0
        ? (util.reduce((s, r) => s + r.hrs, 0) / util.length).toFixed(1)
        : '—';

    const mroCount = mro.length;
    const avgMroDays =
      mro.length > 0
        ? Math.round(mro.reduce((s, e) => s + e.days, 0) / mro.length)
        : 0;

    const criticalCount = util.filter((r) => r.hrs < 5).length;

    return { fleetSize, activeCount, avgDailyHrs, mroCount, avgMroDays, criticalCount };
  }, [fleet, util, mro]);

  // ── Status chart data ──────────────────────────────────────────────────────
  const statusData = useMemo(() => {
    const counts = {};
    fleet.forEach((a) => { counts[a.S] = (counts[a.S] || 0) + 1; });
    const labels = Object.keys(counts);
    const data = labels.map((l) => counts[l]);
    const colors = labels.map((l) => STATUS_COLORS[l] || '#8faabb');
    return { labels, data, colors };
  }, [fleet]);

  const activeCount  = fleet.filter((a) => a.S === 'Active' || a.S === 'On Lease').length;
  const storedCount  = fleet.filter((a) => a.S === 'Stored').length;
  const maintCount   = fleet.filter((a) => a.S === 'Maintenance' || a.S === 'On repair').length;
  const activeRate   = fleet.length ? Math.round((activeCount / fleet.length) * 100) : 0;

  // ── Role chart data ────────────────────────────────────────────────────────
  const roleData = useMemo(() => {
    const counts = {};
    fleet.forEach((a) => { counts[a.Role] = (counts[a.Role] || 0) + 1; });
    const labels = Object.keys(counts);
    const data = labels.map((l) => counts[l]);
    const colors = labels.map((l) => ROLE_COLORS[l] || '#8faabb');
    return { labels, data, colors };
  }, [fleet]);

  // ── Variant chart data ─────────────────────────────────────────────────────
  const variantData = useMemo(() => {
    const counts = {};
    fleet.forEach((a) => { counts[a.V] = (counts[a.V] || 0) + 1; });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8);
    const labels = sorted.map(([v]) => v);
    const data = sorted.map(([, c]) => c);
    const colors = labels.map((_, i) => {
      const palette = ['#1246d6', '#0698a0', '#7c3aed', '#d97706', '#dc2626', '#16a34a', '#ea580c', '#8faabb'];
      return palette[i % palette.length];
    });
    return { labels, data, colors };
  }, [fleet]);

  // ── Engine family chart data ───────────────────────────────────────────────
  const engineData = useMemo(() => {
    const counts = {};
    fleet.forEach((a) => {
      const fam = engineFamily(a.Eng);
      counts[fam] = (counts[fam] || 0) + 1;
    });
    const labels = Object.keys(counts);
    const data = labels.map((l) => counts[l]);
    const colors = labels.map((l) => ENGINE_COLORS[l] || '#8faabb');
    return { labels, data, colors };
  }, [fleet]);

  // ── Chart.js dataset builders ──────────────────────────────────────────────
  const statusChartData = {
    labels: statusData.labels,
    datasets: [{ data: statusData.data, backgroundColor: statusData.colors, borderWidth: 0 }],
  };

  const roleChartData = {
    labels: roleData.labels,
    datasets: [{ data: roleData.data, backgroundColor: roleData.colors, borderWidth: 0 }],
  };

  const variantChartData = {
    labels: variantData.labels,
    datasets: [{
      label: 'Aircraft',
      data: variantData.data,
      backgroundColor: variantData.colors,
      borderRadius: 3,
      borderSkipped: false,
    }],
  };

  const engineChartData = {
    labels: engineData.labels,
    datasets: [{ data: engineData.data, backgroundColor: engineData.colors, borderWidth: 0 }],
  };

  return (
    <div className="page-content">

      {/* ── KPI Row ──────────────────────────────────────────────────────── */}
      <div className="krow krow-5">
        <KPICard
          label="Fleet Size"
          value={kpi.fleetSize}
          sub="Total registered aircraft"
          delta="+3.2%"
          deltaDir="up"
          accent="kb"
        />
        <KPICard
          label="Active Aircraft"
          value={kpi.activeCount}
          sub="Active or on-lease"
          delta="+1.8%"
          deltaDir="up"
          accent="kg"
        />
        <KPICard
          label="Avg Daily Hours"
          value={`${kpi.avgDailyHrs}h`}
          sub="Fleet-wide daily utilisation"
          accent="kt"
        />
        <KPICard
          label="MRO Events"
          value={kpi.mroCount}
          sub={`Avg ${kpi.avgMroDays} days / event`}
          accent="ka"
        />
        <KPICard
          label="Critical Risks"
          value={kpi.criticalCount}
          sub="Aircraft below 5 hrs/day"
          accent="kr"
          alert={kpi.criticalCount > 0}
        />
      </div>

      {/* ── Row 1: Map + Status / Role ───────────────────────────────────── */}
      <div className="g3">

        {/* Fleet Map */}
        <Card
          title="Fleet Last Known Location"
          icon="🗺️"
          iconClass="ib"
          subtitle={`${LIVE.length} assets tracked`}
          noPad
        >
          <FleetMap />
        </Card>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Fleet Status */}
          <Card title="Fleet Status" icon="📊" iconClass="it">
            <div className="cwsm">
              <Doughnut data={statusChartData} options={doughnutOpts('Status')} />
            </div>
            <DonutLegend
              labels={statusData.labels}
              data={statusData.data}
              colors={statusData.colors}
            />
            <div className="msts">
              <div className="mst">
                <div className="msv" style={{ color: 'var(--gn)' }}>{activeCount}</div>
                <div className="msl">Active</div>
              </div>
              <div className="mst">
                <div className="msv" style={{ color: 'var(--am)' }}>{storedCount}</div>
                <div className="msl">Stored</div>
              </div>
              <div className="mst">
                <div className="msv" style={{ color: 'var(--acc)' }}>{maintCount}</div>
                <div className="msl">MRO</div>
              </div>
              <div className="mst">
                <div className="msv">{activeRate}%</div>
                <div className="msl">Active rate</div>
              </div>
            </div>
          </Card>

          {/* Aircraft Role Split */}
          <Card title="Aircraft Role Split" icon="✈" iconClass="ip">
            <div className="cwxs">
              <Doughnut data={roleChartData} options={doughnutOpts('Role')} />
            </div>
            <DonutLegend
              labels={roleData.labels}
              data={roleData.data}
              colors={roleData.colors}
            />
          </Card>

        </div>
      </div>

      {/* ── Row 2: Variant Bar + Engine Doughnut ────────────────────────── */}
      <div className="g2">

        {/* Fleet by Variant */}
        <Card title="Fleet by Variant" icon="📦" iconClass="ib">
          <div className="cwsm">
            <Bar data={variantChartData} options={hBarOpts} />
          </div>
        </Card>

        {/* Engine Family Distribution */}
        <Card title="Engine Family Distribution" icon="⚙️" iconClass="it">
          <div className="cwsm">
            <Doughnut data={engineChartData} options={doughnutOpts('Engine')} />
          </div>
          <DonutLegend
            labels={engineData.labels}
            data={engineData.data}
            colors={engineData.colors}
          />
        </Card>

      </div>
    </div>
  );
}
